import React, { createContext, useContext, useState, useEffect } from 'react';
import API_URL from '../config';
import { SERVICE_CATEGORIES } from '../data/services';

const AppointmentContext = createContext();

// Session duration: 1 hour (in milliseconds)
const ADMIN_SESSION_MS = 60 * 60 * 1000;

// Helper to check 1-hour session validity
const getInitialAdminAuth = () => {
  const isLoggedIn = localStorage.getItem('adminLoggedIn') === 'true';
  const loginTime = parseInt(localStorage.getItem('adminLoginTime') || '0', 10);
  if (isLoggedIn && loginTime && (Date.now() - loginTime < ADMIN_SESSION_MS)) {
    return true;
  }
  localStorage.removeItem('adminLoggedIn');
  localStorage.removeItem('adminLoginTime');
  return false;
};

// ─── Default Weekly Schedule ────────────────────────────────────────────────
export const defaultSchedule = {
  0: [], // Sunday – closed
  1: ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'],
  2: ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'],
  3: ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM'],
  4: ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'],
  5: ['09:00 AM', '10:00 AM', '11:00 AM', '01:00 PM', '02:00 PM'],
  6: ['10:00 AM', '11:00 AM', '12:00 PM'], // Saturday – limited
};

// Helper to normalize schedule format to { 0: [...], 1: [...], ... 6: [...] }
export const normalizeSchedule = (raw) => {
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch (e) {}
  }
  if (!raw || typeof raw !== 'object') return { ...defaultSchedule };
  const normalized = {};
  for (let i = 0; i <= 6; i++) {
    const val = raw[i] !== undefined ? raw[i] : raw[String(i)];
    normalized[i] = Array.isArray(val) ? [...val] : [];
  }
  return normalized;
};

// ─── Provider ────────────────────────────────────────────────────────────────
export const AppointmentProvider = ({ children }) => {
  const [appointments, setAppointments] = useState([]);
  const [schedule, setSchedule] = useState(() => {
    try {
      const cached = localStorage.getItem('site_schedule');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === 'object') {
          return normalizeSchedule(parsed);
        }
      }
    } catch (e) {}
    return defaultSchedule;
  });
  const [blockedDates, setBlockedDates] = useState(() => {
    try {
      const cached = localStorage.getItem('site_blocked_dates');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {}
    return [];
  });
  const [blogs, setBlogs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [servicesList, setServicesList] = useState(() => {
    const cached = localStorage.getItem('site_services');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) {}
    }
    return SERVICE_CATEGORIES;
  });
  const [adminLoggedIn, setAdminLoggedIn] = useState(getInitialAdminAuth);
  const [loading, setLoading] = useState(true);

  // ── Load data from API on start ───────────────────────────────────────────
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Fetch Blogs
        const blogsRes = await fetch(`${API_URL}/api/blog`);
        const blogsData = await blogsRes.json();
        if (blogsData.success && Array.isArray(blogsData.data)) setBlogs(blogsData.data);

        // Fetch Appointments
        const apptsRes = await fetch(`${API_URL}/api/appointments`);
        const apptsData = await apptsRes.json();
        if (apptsData.success && Array.isArray(apptsData.data)) setAppointments(apptsData.data);

        // Fetch Messages
        const msgRes = await fetch(`${API_URL}/api/contact`);
        const msgData = await msgRes.json();
        if (msgData.success && Array.isArray(msgData.data)) setMessages(msgData.data);

        // Fetch Schedule — normalize keys to numbers so schedule[dayOfWeek] always works
        try {
          const schedRes = await fetch(`${API_URL}/api/settings/schedule`);
          const schedData = await schedRes.json();
          if (schedData.success && schedData.data !== undefined) {
            const normalized = normalizeSchedule(schedData.data);
            setSchedule(normalized);
            localStorage.setItem('site_schedule', JSON.stringify(normalized));
          }
        } catch (schedErr) {
          console.warn('Could not fetch schedule from backend, using cache/defaults:', schedErr);
        }

        // Fetch Blocked Dates
        try {
          const blockRes = await fetch(`${API_URL}/api/settings/blockedDates`);
          const blockData = await blockRes.json();
          if (blockData.success && blockData.data !== undefined) {
            let blockArray = blockData.data;
            if (typeof blockArray === 'string') {
              try {
                blockArray = JSON.parse(blockArray);
              } catch (e) {}
            }
            if (Array.isArray(blockArray)) {
              setBlockedDates(blockArray);
              localStorage.setItem('site_blocked_dates', JSON.stringify(blockArray));
            }
          }
        } catch (blockErr) {
          console.warn('Could not fetch blocked dates from backend:', blockErr);
        }

        // Fetch Services Settings
        try {
          const svcRes = await fetch(`${API_URL}/api/settings/services`);
          const svcData = await svcRes.json();
          if (svcData.success && Array.isArray(svcData.data) && svcData.data.length > 0) {
            setServicesList(svcData.data);
            localStorage.setItem('site_services', JSON.stringify(svcData.data));
          }
        } catch (svcErr) {
          // Uses initial state
        }
      } catch (err) {
        console.error('Error loading database data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // ── Appointment Actions ────────────────────────────────────────────────────
  const addAppointment = async (appt) => {
    try {
      const response = await fetch(`${API_URL}/api/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appt),
      });
      const json = await response.json();
      if (json.success) {
        setAppointments((prev) => [json.data, ...prev]);
        return json.data;
      } else {
        alert(json.message || 'Failed to book slot.');
        return null;
      }
    } catch (error) {
      console.error('Error booking appointment:', error);
      alert('Error connecting to backend API.');
      return null;
    }
  };

  const updateAppointmentStatus = async (id, status) => {
    try {
      const response = await fetch(`${API_URL}/api/appointments/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const json = await response.json();
      if (json.success) {
        setAppointments((prev) =>
          prev.map((a) => (a.id === id ? { ...a, status } : a))
        );
      }
    } catch (error) {
      console.error('Error updating appointment status:', error);
    }
  };

  const deleteAppointment = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/appointments/${id}`, {
        method: 'DELETE',
      });
      const json = await response.json();
      if (json.success) {
        setAppointments((prev) => prev.filter((a) => a.id !== id));
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
    }
  };

  // ── Schedule Actions ───────────────────────────────────────────────────────
  const updateSchedule = async (newSchedule) => {
    try {
      const normalizedToSend = normalizeSchedule(newSchedule);
      // Optimistic state & local storage update
      setSchedule(normalizedToSend);
      localStorage.setItem('site_schedule', JSON.stringify(normalizedToSend));

      const response = await fetch(`${API_URL}/api/settings/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: normalizedToSend }),
      });
      const json = await response.json();
      if (json.success) {
        const saved = normalizeSchedule(json.data || normalizedToSend);
        setSchedule(saved);
        localStorage.setItem('site_schedule', JSON.stringify(saved));
        return { success: true, data: saved };
      } else {
        throw new Error(json.message || 'Server rejected schedule update');
      }
    } catch (error) {
      console.error('Error updating schedule:', error);
      throw error;
    }
  };

  const updateDaySchedule = async (dayIndex, slots) => {
    const next = { ...schedule, [dayIndex]: slots };
    return await updateSchedule(next);
  };

  const resetScheduleToDefault = async () => {
    return await updateSchedule(defaultSchedule);
  };

  const toggleBlockedDate = async (dateStr) => {
    try {
      const safeBlocked = Array.isArray(blockedDates) ? blockedDates : [];
      const nextBlocked = safeBlocked.includes(dateStr)
        ? safeBlocked.filter((d) => d !== dateStr)
        : [...safeBlocked, dateStr].sort();

      setBlockedDates(nextBlocked);
      localStorage.setItem('site_blocked_dates', JSON.stringify(nextBlocked));

      const response = await fetch(`${API_URL}/api/settings/blockedDates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: nextBlocked }),
      });
      const json = await response.json();
      if (json.success) {
        const data = Array.isArray(json.data) ? json.data : nextBlocked;
        setBlockedDates(data);
        localStorage.setItem('site_blocked_dates', JSON.stringify(data));
        return { success: true, data };
      }
      return { success: true, data: nextBlocked };
    } catch (error) {
      console.error('Error toggling blocked date:', error);
      throw error;
    }
  };

  // ── Availability Helpers ───────────────────────────────────────────────────
  const getAvailableSlots = (dateStr) => {
    if (!dateStr) return [];
    const d = new Date(dateStr + 'T00:00:00');
    const dayOfWeek = d.getDay();
    // Defensive: ensure blockedDates is always an array
    const safeBlocked = Array.isArray(blockedDates) ? blockedDates : [];
    if (safeBlocked.includes(dateStr)) return [];
    // Support both numeric and string keys (MySQL JSON returns string keys)
    const rawSlots = schedule[dayOfWeek] !== undefined
      ? schedule[dayOfWeek]
      : schedule[String(dayOfWeek)];
    const daySlots = Array.isArray(rawSlots) ? rawSlots : [];
    // Defensive: ensure appointments is always an array
    const safeAppts = Array.isArray(appointments) ? appointments : [];
    const bookedSlots = safeAppts
      .filter((a) => a.date === dateStr && a.status !== 'cancelled')
      .map((a) => a.time);
    return daySlots.filter((s) => !bookedSlots.includes(s));
  };

  const isDateAvailable = (dateStr) => {
    // Defensive: ensure blockedDates is always an array
    const safeBlocked = Array.isArray(blockedDates) ? blockedDates : [];
    if (safeBlocked.includes(dateStr)) return false;
    const d = new Date(dateStr + 'T00:00:00');
    const dayOfWeek = d.getDay();
    // Support both numeric and string keys (MySQL JSON returns string keys)
    const rawSlots = schedule[dayOfWeek] !== undefined
      ? schedule[dayOfWeek]
      : schedule[String(dayOfWeek)];
    const daySlots = Array.isArray(rawSlots) ? rawSlots : [];
    return daySlots.length > 0;
  };

  // ── Blog Actions ───────────────────────────────────────────────────────────
  const addBlog = async (blog) => {
    try {
      const response = await fetch(`${API_URL}/api/blog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(blog),
      });
      const json = await response.json();
      if (json.success && json.data) {
        setBlogs((prev) => [json.data, ...prev]);
        return { success: true, data: json.data };
      } else {
        return { success: false, message: json.message || 'Failed to add blog post' };
      }
    } catch (error) {
      console.error('Error adding blog:', error);
      return { success: false, message: error.message || 'Network error connecting to backend' };
    }
  };

  const updateBlog = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/blog/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      const json = await response.json();
      if (json.success && json.data) {
        setBlogs((prev) =>
          prev.map((b) => (String(b.id) === String(id) ? { ...b, ...json.data } : b))
        );
        return { success: true, data: json.data };
      } else {
        return { success: false, message: json.message || 'Failed to update blog post' };
      }
    } catch (error) {
      console.error('Error updating blog:', error);
      return { success: false, message: error.message || 'Network error connecting to backend' };
    }
  };

  const deleteBlog = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/blog/${id}`, {
        method: 'DELETE',
      });
      const json = await response.json();
      if (json.success) {
        setBlogs((prev) => prev.filter((b) => String(b.id) !== String(id)));
        return { success: true };
      } else {
        return { success: false, message: json.message || 'Failed to delete blog post' };
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
      return { success: false, message: error.message || 'Network error deleting blog post' };
    }
  };

  // ── Contact Inquiries Actions ──────────────────────────────────────────────
  const deleteMessage = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/contact/${id}`, {
        method: 'DELETE',
      });
      const json = await response.json();
      if (json.success) {
        setMessages((prev) => prev.filter((m) => m.id !== id));
      }
    } catch (error) {
      console.error('Error deleting contact message:', error);
    }
  };

  // ── Services Actions ────────────────────────────────────────────────────────
  const updateServices = async (newServices) => {
    setServicesList(newServices);
    localStorage.setItem('site_services', JSON.stringify(newServices));
    try {
      const res = await fetch(`${API_URL}/api/settings/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: newServices }),
      });
      return await res.json();
    } catch (err) {
      console.error('Error saving services setting:', err);
      return { success: false, message: err.message };
    }
  };

  const resetServicesToDefault = async () => {
    return await updateServices(SERVICE_CATEGORIES);
  };

  // ── Admin Auth with 1-Hour Session Auto-Expiry ─────────────────────────────
  useEffect(() => {
    if (!adminLoggedIn) return;

    const checkSession = () => {
      const loginTime = parseInt(localStorage.getItem('adminLoginTime') || '0', 10);
      if (!loginTime || Date.now() - loginTime >= ADMIN_SESSION_MS) {
        setAdminLoggedIn(false);
        localStorage.removeItem('adminLoggedIn');
        localStorage.removeItem('adminLoginTime');
      }
    };

    // Check every 15 seconds
    const interval = setInterval(checkSession, 15000);
    return () => clearInterval(interval);
  }, [adminLoggedIn]);

  const adminLogin = (adminId, password) => {
    if (adminId === 'Adityaadmin' && password === 'Aditya@#123') {
      const now = Date.now();
      setAdminLoggedIn(true);
      localStorage.setItem('adminLoggedIn', 'true');
      localStorage.setItem('adminLoginTime', String(now));
      return true;
    }
    return false;
  };

  const adminLogout = () => {
    setAdminLoggedIn(false);
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminLoginTime');
  };

  return (
    <AppointmentContext.Provider
      value={{
        // Loading state
        loading,
        // Appointments
        appointments,
        addAppointment,
        updateAppointmentStatus,
        deleteAppointment,
        // Schedule
        schedule,
        blockedDates,
        updateSchedule,
        updateDaySchedule,
        resetScheduleToDefault,
        toggleBlockedDate,
        getAvailableSlots,
        isDateAvailable,
        defaultSchedule,
        normalizeSchedule,
        // Blogs
        blogs,
        addBlog,
        updateBlog,
        deleteBlog,
        // Contact messages
        messages,
        deleteMessage,
        // Services
        servicesList,
        updateServices,
        resetServicesToDefault,
        // Admin
        adminLoggedIn,
        adminLogin,
        adminLogout,
      }}
    >
      {children}
    </AppointmentContext.Provider>
  );
};

export const useAppointment = () => {
  const ctx = useContext(AppointmentContext);
  if (!ctx) throw new Error('useAppointment must be used inside AppointmentProvider');
  return ctx;
};
